<template>
  <v-container fluid>

    <!-- Page Title -->
    <app-page-title
      v-bind:title="'Animal Sources'"
      v-bind:icon="'color_lens'"
    />

    <v-data-table
      :headers="headers"
      :items="animalSources"
      :loading="isLoading"
      :search="search"
      :no-data-text="noDataText"
      mobile-breakpoint="500"
      >
      <v-progress-linear v-slot:progress :color="appColor_Progress" indeterminate />

      <!-- Edit Dialog -->
      <template v-slot:top>
        <v-toolbar
          dense
          dark
          :color="appColor_Medium"
          >
          <v-text-field
            v-model="search"
            append-icon="search"
            label="Search"
            single-line
            hide-details
            class="pb-3"
          />
          <v-spacer />
          <v-toolbar-items>
            <v-dialog
              v-model="editDialog"
              max-width="500px"
              :persistent="true"
              >
              <v-card>
                <v-toolbar dark flat :color="appColor_Light">
                  <v-icon class="mr-2">
                    edit
                  </v-icon> 
                  <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
                  <v-spacer />
                  <v-chip dark :color="editedSource.colourCode" class="elevation-2">
                    {{ editedSource.cosmosContainerId }}
                  </v-chip>
                </v-toolbar>
                <v-card-text class="pt-0 pb-0 pl-3 pr-3">
                  <v-container grid-list-md class="pt-0 pb-0">
                    <form>
                      <v-row no-gutters>
                        <v-col
                          cols="12"
                          xs="10"
                          sm="10"
                          md="10"
                          >
                          <v-text-field
                            v-model="editedSource.name"
                            label="Name"
                            hide-details
                            disabled
                            class="py-4 mt-4"
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          xs="2"
                          sm="2"
                          md="2"
                          >
                          <v-text-field
                            v-model="editedSource.cosmosContainerId"
                            label="Code"
                            hide-details
                            disabled
                            class="py-4 mt-4 ml-3"
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          xs="12"
                          sm="12"
                          md="12"
                          >
                          <v-text-field
                            v-model="editedSource.description"
                            label="Description"
                            hide-details
                            disabled
                            class="py-4"
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          xs="5"
                          sm="5"
                          md="5"
                          >
                          <v-select
                            v-model="editedSource.colourCode"
                            label="Colour Code"
                            :items="colours"
                            outlined
                            hint="Pick a colour to view the sample"
                            persistent-hint
                            hide-details
                            class="py-4 mb-2"
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          xs="7"
                          sm="7"
                          md="7"
                          >
                          <v-card :color="editedSource.colourCode + ' lighten-4 pa-2 ml-3 mb-4 elevation-0'">
                            <v-card dark class="elevation-2">
                              <v-toolbar flat dark dense :color="editedSource.colourCode + ' darken-4'" height="25">
                                <v-toolbar-title class="body-2 white--text">{{ editedSource.cosmosContainerId + ' Query' }}</v-toolbar-title>
                              </v-toolbar>
                              <v-card-text :class="editedSource.colourCode + ' darken-2 caption white--text pa-2 pl-4'">
                                {{ editedSource.description }}
                              </v-card-text>
                            </v-card>
                          </v-card>
                        </v-col>
                      </v-row>
                    </form>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    :disabled="isSaving"
                    text
                    @click="closeDialog"
                    >
                    Cancel
                  </v-btn>
                  <v-btn
                    :color="appColor_ButtonOK"
                    :disabled="isSaving"
                    text
                    @click="save"
                    >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>
      </template>

      <template v-slot:item.logoUrl="{ item }">
        <v-img large :src="item.logoUrl" width="90"></v-img>
      </template>

      <template v-slot:item.colourCode="{ item }">
        <v-icon large :color="item.colourCode">lens</v-icon>
      </template>

      <template v-slot:item.name="{ item }">
        <span class="text-left">
          <router-link to="/animalSourceManager">
            <span @click="edit(item)">{{ item.name }}</span>
          </router-link>
        </span>
      </template>

    </v-data-table>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppPageTitle from '@/components/AppPageTitle'
import { mapGetters } from 'vuex'
import { AppColor } from '@/config.js'

export default {
  name: 'AnimalSourceManager',
  components: {
    AppPageTitle
  },
  data () {
    return {
      appColor_Light: AppColor.Light,
      appColor_Medium: AppColor.Medium,
      appColor_Background: AppColor.Background,
      appColor_Progress: AppColor.Progress,
      appColor_ButtonOK: AppColor.ButtonOK,

      isLoading: true,
      isSaving: false,
      editedIndex: -1,
      editDialog: false,
      search: '',
      defaultSource: {
        id: -1,
        name: null,
        description: null,
        cosmosContainerId: null,
        colourCode: null
      },
      editedSource: {
        id: -1,
        name: null,
        description: null,
        cosmosContainerId: null,
        colourCode: null
      },
      animalSources: [],
      colours: [],
      headers: [
        {
          value: 'logoUrl',
          align: 'center',
          width: '10%'
        },
        {
          value: 'colourCode',
          align: 'center',
          width: '5%'
        },
        {
          text: 'Name',
          value: 'name',
          align: 'left',
          width: '35%'
        },
        {
          text: 'Description',
          value: 'description',
          align: 'left',
          width: '40%'
        },
        {
          text: 'Code',
          value: 'cosmosContainerId',
          align: 'left',
          width: '10%'
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['profile']),
    noDataText () {
      return this.isLoading
        ? 'One moment...'
        : 'No animal sources found'
    },
    formTitle () {
      return this.editedSource.id > 0 ? 'Edit Source' : 'New Source'
    }
  },
  methods: {
    loadData () {
      const self = this
      self.isLoading = true
      appService.getAnimalSources(true).then(data => {
        if (data.errSeverity > 0) {
          self.errorText = data.errorMessage
          self.errorDialog = true
        } else {
          self.animalSources = data.sources
          self.colours = data.colours
        }
        self.isLoading = false
      })
    },

    edit (item) {
      this.editedIndex = this.animalSources.indexOf(item)
      this.editedSource = Object.assign({}, item)
      this.editDialog = true
    },

    save () {
      const self = this
      var editedSource = self.animalSources[self.editedIndex]
      Object.assign(editedSource, self.editedSource)
      const sourceModel = self.getModel(editedSource)
      appService.updateAnimalSource(editedSource.id, sourceModel).then((data) => {
        if (data.errSeverity > 0) {
          self.errorText = data.errorMessage
          self.errorDialog = true
        } else {
          self.animalSources[self.editedIndex].colourCode = data.source.colourCode
        }
        self.isSaving = false
        self.closeDialog()
      })
    },

    getModel (s) {
      return {
          // name: s.name,
          // description: s.description,
          // cosmosContainerId: s.cosmosContainerId,
          colourCode: s.colourCode
        }
    },

    closeDialog () {
      const self = this
      self.editDialog = false
      setTimeout(() => {
        self.resetEditedSource()
      }, 300)
    },

    resetEditedSource () {
      this.editedSource = Object.assign({}, this.defaultSource)
      this.editedIndex = -1
    },
  },
  created () {
    this.loadData()
  }
}
</script>
